import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { App } from '@df/core/app';
import { resizeObservable } from '@df/dom/fn/resize-observable';
import { Subscription } from 'rxjs';
import { ElementHeightService, IElementHeightEntryOptions } from './element-height.service';

@Directive({
    selector: '[elementHeight]',
    standalone: true
})
export class ElementHeightDirective implements AfterViewInit, OnDestroy, OnChanges {
    @Input({ required: true })
    elementHeight!: string;

    @Input()
    elementHeightOptions?: IElementHeightEntryOptions;

    @Output()
    elementHeightUpdate = new EventEmitter<number | undefined>();

    private subscriptions = new Subscription();

    private set value(value: number | undefined) {
        const target = this.elementHeightOptions?.local ? this.elementRef.nativeElement : document.body;

        if (typeof value === 'number') {
            target.style.setProperty(this.property, `${Math.round(value * 100) / 100}px`);
        } else {
            target.style.removeProperty(this.property);
        }

        if (!this.elementHeightOptions?.local) {
            this.elementHeightService.getState(this.elementHeight).next(value);
            this.elementHeightUpdate.next(value);
        }
    }

    get value() {
        return this.elementHeightService.getState(this.elementHeight)!.value!;
    }

    constructor(
        protected elementRef: ElementRef<HTMLElement>,
        private app: App,
        private elementHeightService: ElementHeightService
    ) {}

    ngAfterViewInit(): void {
        if (this.app.isPlatformBrowser) {
            this.subscriptions.add(
                resizeObservable(
                    this.elementRef.nativeElement,
                    (event: ResizeObserverEntry) => {
                        this.value = event.borderBoxSize[0].blockSize;
                    },
                    this.elementHeightOptions?.resizeDebounceTime
                ).subscribe()
            );
        }
    }

    ngOnDestroy(): void {
        if (this.app.isPlatformBrowser) {
            this.value = undefined;
        }

        this.subscriptions.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['elementHeight']) {
            this.elementHeight = this.elementHeight?.toLowerCase();
        }

        if (!this.elementHeightOptions?.local) {
            this.elementHeightService.register(this.elementHeight, this.elementHeightOptions);
        }
    }

    private get property() {
        return `--element-height-${this.elementHeight}`;
    }
}
